import React from 'react';
import { cssVar, HStack, VStack, Text, Popover, PopoverContent, PopoverTrigger } from '@cad-arc';
import { EmptyPropsWithChildren } from '@m43/core';
import { useConnectionStatus } from '../../connection-status/use-connection-status';
import { ConnectionStatusEnumType, ConnectionStatusSourceEnumType } from '@mark43/cad-api';

export const connectionStatusStyles: Record<
    ConnectionStatusEnumType,
    { fg: string; bg: string }
> = {
    ONLINE: {
        fg: 'arc.colors.positive.default',
        bg: 'arc.colors.positive.accent',
    },
    DEGRADED: {
        fg: 'arc.colors.warning.default',
        bg: 'arc.colors.warning.accent',
    },
    OFFLINE: {
        fg: 'arc.colors.negative.default',
        bg: 'arc.colors.negative.accent',
    },
} as const;

const connectionStatusSourceToDisplay: Record<ConnectionStatusSourceEnumType, string> = {
    MARK43_PING: 'Mark43 Ping',
    INTERNET: 'Internet',
    SOCKET: 'Realtime',
    DNS_RESOLUTION: 'DNS Resolution',
    FARO: 'Monitoring',
};

const connectionStatusToDisplay: Record<ConnectionStatusEnumType, string> = {
    DEGRADED: 'Degraded',
    ONLINE: 'Online',
    OFFLINE: 'Offline',
};

function ConnectionStatusCircle(props: { connectionStatus: ConnectionStatusEnumType }) {
    return (
        <div
            style={{
                flexShrink: 0,
                width: '10px',
                height: '10px',
                borderRadius: '10px',
                display: 'inline-block',
                backgroundColor: cssVar(connectionStatusStyles[props.connectionStatus].fg as any),
            }}
        />
    );
}

const PLACEHOLDER = '—';

export function ConnectionStatusIndicator({ children }: EmptyPropsWithChildren) {
    const connectionStatus = useConnectionStatus();
    let connectionStatusToUse: ConnectionStatusEnumType = 'ONLINE';
    for (const connectionStatusValue of Object.values(connectionStatus).map(
        (status) => status
    ) as ConnectionStatusEnumType[]) {
        if (connectionStatusValue === 'OFFLINE') {
            connectionStatusToUse = 'OFFLINE';
            break;
        }
        if (connectionStatusValue === 'DEGRADED') {
            connectionStatusToUse = 'DEGRADED';
        }
    }
    return (
        <Popover>
            <PopoverTrigger>
                {children ? (
                    <HStack padding="3px 4px">
                        <ConnectionStatusCircle connectionStatus={connectionStatusToUse} />
                        {children}
                    </HStack>
                ) : (
                    <ConnectionStatusCircle connectionStatus={connectionStatusToUse} />
                )}
            </PopoverTrigger>
            <PopoverContent>
                <VStack alignItems="flex-start">
                    {Object.entries(connectionStatus)
                        .filter(
                            ([connectionStatusSource]) =>
                                connectionStatusSource !== 'DNS_RESOLUTION' &&
                                connectionStatusSource !== 'FARO'
                        )
                        .map(([connectionStatusSource, connectionStatus]) => (
                            <HStack key={connectionStatusSource}>
                                <Text fontSize="lg" color="text.placeholder">
                                    {
                                        connectionStatusSourceToDisplay[
                                            connectionStatusSource as ConnectionStatusSourceEnumType
                                        ]
                                    }
                                    :
                                </Text>
                                <Text fontSize="lg" color="inherit">
                                    {connectionStatusToDisplay[connectionStatus] ?? PLACEHOLDER}
                                </Text>
                                <ConnectionStatusCircle connectionStatus={connectionStatus} />
                            </HStack>
                        ))}
                </VStack>
            </PopoverContent>
        </Popover>
    );
}
